import { fetchAuthSession } from 'aws-amplify/auth';
import { refreshWithStoredToken } from '@/lib/cognito';
import { debug, warn } from '@/utils/logger';
import { withTimeout } from '@/utils/withTimeout';

export const REFRESH_TIMEOUT_MS = 15000;

export type RefreshAccessTokenResult =
  | { ok: true; accessToken: string }
  | { ok: false; shouldLogout: boolean };

export async function refreshAccessToken(
  logPrefix = 'refreshAccessToken',
): Promise<RefreshAccessTokenResult> {
  try {
    debug(`🔐 [${logPrefix}] Attempting to refresh tokens via Amplify`);

    const session = await withTimeout(
      fetchAuthSession({ forceRefresh: true }),
      REFRESH_TIMEOUT_MS,
      'Amplify fetchAuthSession timed out',
    );

    const newAccessToken = session.tokens?.accessToken?.toString();
    const newIdToken = session.tokens?.idToken?.toString();

    if (newAccessToken && newIdToken) {
      debug(`🔐 [${logPrefix}] Token refresh successful via Amplify`);
      localStorage.setItem('accessToken', newAccessToken);
      localStorage.setItem('idToken', newIdToken);
      return { ok: true, accessToken: newAccessToken };
    }

    warn(`🔐 [${logPrefix}] Amplify returned empty tokens, trying stored refresh token`);
    return refreshViaStoredToken(logPrefix);
  } catch (error) {
    warn(`🔐 [${logPrefix}] Amplify refresh threw, trying stored refresh token:`, error);
    return refreshViaStoredToken(logPrefix);
  }
}

async function refreshViaStoredToken(logPrefix: string): Promise<RefreshAccessTokenResult> {
  const fallback = await withTimeout(
    refreshWithStoredToken(),
    REFRESH_TIMEOUT_MS,
    'Stored token refresh timed out',
  ).catch(() => ({ ok: false as const, reason: 'transient' as const }));

  if (fallback.ok) {
    return { ok: true, accessToken: fallback.accessToken };
  }

  const shouldLogout = fallback.reason === 'rejected' || fallback.reason === 'missing';
  debug(`🔐 [${logPrefix}] Stored refresh failed`, { reason: fallback.reason, shouldLogout });
  return { ok: false, shouldLogout };
}
